import React, { forwardRef, useContext } from 'react';
import log from '../../utils/log';
import Field from '../form/engine/FieldEngine';
import styled from 'styled-components/macro';
import Description from '../form/Description';
import Button from '../Button';
import useTranslations from '../../hooks/useTranslations';
import Divider from '@material-ui/core/Divider';
import SaveChanges, {
  IconsCSS,
  SaveChangesButton,
  SCIcon
} from '../form/SaveChanges';
import ThankYouEngine from '../form/engine/ThankYouEngine';
import Animation from '../Animation';
import IgnoreValidationDialog from '../../internal-components/GeneralDialog';
import CredasWarningDialog from '../../internal-components/GeneralDialog';
import ThirdfortWarningDialog from '../../internal-components/GeneralDialog';
import WorkInProgressBanner from '../form/WorkInProgressBanner';
import { Sizes } from '../layouts/ng-layouts/Containers';
import { Variants } from '../engine/FormEngine';
import useGlobalVariables from '../../hooks/useGlobalVariables';
import { getButtonStyle } from '../engine/CustomButtonWizardEngine';
import MultipurposeButtonView from './MultipurposeButtonView';
import MultipurposeButtonEngine from '../engine/MultipurposeButtonEngine';
import FormTOCEngine from '../engine/FormTOCEngine';
import {
  SYSTEM_BUTTONS_DEFAULT_SCHEMA,
  INTEGRATION_BUTTONS_DEFAULT_SCHEMA
} from '../engine/FormBuilderCustomButtonsEngine';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 760px;
  margin: auto;
  ${({ additionalCSS }) => additionalCSS}
`;

const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  background: #fff;

  form {
    ${({ $readOnly }) => $readOnly && 'opacity: 0.7;'}
  }
`;

const FormTitle = styled.h2`
  margin: 1.8em 0 0.8em 0;
  padding: 0 3rem;
`;

const BottomEndpointContainer = styled.div`
  background: #fff;
  display: flex;
  flex-direction: column;
  padding: 0 0.8em;
  bottom: 0;
  z-index: 10;
  ${({ $isSticky }) => $isSticky && 'position: sticky;'}
`;

const TopEndpointContainer = styled(BottomEndpointContainer)``;

// use flex to change layout!!! VVV
const TopEndpointContainerAnimation = styled(Animation)`
  ${({ $isSticky }) => $isSticky && 'position: sticky;'}
  top: ${({ $isMobile }) =>
    $isMobile ? Sizes.MOBILE_NAV_BAR_HEIGHT : Sizes.NAV_BAR_HEIGHT}px;
  z-index: 9;
`;

const BtnsContainer = styled.div`
  padding: 0.8em 0;
  display: flex;
  flex-direction: row-reverse;
  flex-wrap: wrap;

  & > button {
    margin: 0.4rem !important;
  }
`;

const TitleAndDescriptionDivider = styled(Divider)`
  margin: 0 0.8em 0.8em !important;
`;

const FormView = forwardRef((props, formRef) => {
  const t = useTranslations();
  const { isMobileView } = useGlobalVariables();
  log('Form view component rendered!');

  return (
    <>
      {props.showTOC ? (
        <FormTOCEngine ref={props.formTOCRef} fields={props.fields} />
      ) : null}
      <Container additionalCSS={props.customCSS}>
        <TopEndpointContainerAnimation
          type="collapse"
          in={!props.isSubmitted}
          $isSticky={props.stickyTopBtns}
          $isMobile={isMobileView}
        >
          <TopEndpointContainer>
            <BtnsContainer>
              {props.topEndPoint ? props.topEndPoint : null}
              {props.stickyTopBtns &&
                (props.useAutoSave ? (
                  <SaveChanges id="save-changes-indicator" />
                ) : props.noSaveBtn ||
                  props.isFormReadOnly ||
                  !props.saveChangesButton ? null : (
                  <SaveChangesButton
                    id={props.saveChangesButton.id}
                    customCSS={`${getButtonStyle(
                      props.saveChangesButton.variant,
                      props.saveChangesButton.color
                    )} margin: 0 auto 0 0;`}
                    onClick={props.saveProgress}
                    variant={props.saveChangesButton.variant}
                  >
                    {t(props.saveChangesButton.label)}
                  </SaveChangesButton>
                ))}
            </BtnsContainer>
            <Divider />
          </TopEndpointContainer>
        </TopEndpointContainerAnimation>
        <FormWrapper $readOnly={props.isFormReadOnly}>
          <Animation type="zoom" in={props.isSubmitted} mountOnEnter>
            {props.thankUCmp ? props.thankUCmp : <ThankYouEngine />}
          </Animation>
          <Animation type="collapse" in={!props.isSubmitted}>
            {props.variant !== Variants.MOBILE &&
              props.form.sharingType === 'internal' &&
              props.isEndUserEndpoint && (
                <WorkInProgressBanner submission={props.userSubmission} />
              )}
            {!props.hideFormTitle && <FormTitle>{props.title}</FormTitle>}
            {!props.hideFormDescription && (
              <Description
                fontSize={13}
                content={props.description}
                additionalCSS={`padding: ${
                  props.variant === Variants.MOBILE ? '0.8em' : '0'
                } 3em 0.8em;`}
              />
            )}
            {(!props.hideFormTitle || !props.hideFormDescription) && (
              <TitleAndDescriptionDivider />
            )}
            <form
              className="ngform"
              autoComplete="off"
              ref={formRef}
              // onChange={props.onFormChange} // Moved into FieldEngine
              onBlur={props.onFormBlur}
              onSubmit={props.onFormSubmit}
            >
              {props.fields.map(field =>
                field.archived ? null : (
                  <Field
                    field={field}
                    key={field.internalId}
                    initialValue={props.userInput[field.internalId]}
                    initialLookupData={props.lookupData}
                  />
                )
              )}
              <BottomEndpointContainer $isSticky={props.stickyBottomBtns}>
                <Divider />
                <BtnsContainer>
                  {/* hidden first button to prevent HTML5 submit
          https://stackoverflow.com/a/51507806/5791449 */}
                  <button
                    type="submit"
                    disabled
                    style={{ display: 'none' }}
                  ></button>
                  <ActionButtons buttons={props.buttons} {...props} />
                  {props.stickyBottomBtns && props.useAutoSave && (
                    <SaveChanges id="save-changes-indicator" />
                  )}
                </BtnsContainer>
              </BottomEndpointContainer>
            </form>
          </Animation>
        </FormWrapper>
      </Container>
      <IgnoreValidationDialog
        open={props.isIgnoreValidationWarningDialogOpen}
        title="dialog.title.warning"
        content="entries.forms.validation.dialog.content"
        handleClose={() => props.setIsIgnoreValidationWarningDialogOpen(false)}
        handleConfirm={() => {
          const buttonId = props.ignoreValidationSubmitButtonId;
          props.setIsIgnoreValidationWarningDialogOpen(false);
          props.setIgnoreValidationSubmitButtonId(null);
          props.submitForm(null, buttonId);
        }}
      />
      <CredasWarningDialog
        open={props.isCredasWarningDialogOpen}
        title="dialog.title.areYouSure"
        content="dialog.credas_warning.content"
        handleClose={() => props.setIsCredasWarningDialogOpen(false)}
        handleConfirm={() => {
          props.saveWithoutWarning();
          props.setIsCredasWarningDialogOpen(false);
        }}
      />
      <ThirdfortWarningDialog
        open={props.isThirdfortWarningDialogOpen}
        title="dialog.title.areYouSure"
        content="dialog.thirdfort_warning.content"
        handleClose={() => props.setIsThirdfortWarningDialogOpen(false)}
        handleConfirm={() => {
          props.saveWithoutWarning();
          props.setIsThirdfortWarningDialogOpen(false);
        }}
      />
    </>
  );
});

export default FormView;

const removeSaveButtonsIfAnonymous = (buttons, props) => {
  if (!props.isAnonymous) {
    return buttons;
  }
  return buttons.filter(
    one =>
      one?.additionalParameters?.root?.action !== 'save' ||
      one?.type === 'admin'
  );
};

const ActionButtons = ({ buttons = [], ...props }) => {
  const t = useTranslations();
  const sbds = SYSTEM_BUTTONS_DEFAULT_SCHEMA;
  const onlyOneButton = buttons.length === 1;
  const filteredButtons = removeSaveButtonsIfAnonymous(buttons, props);
  return filteredButtons.map(b => {
    if (
      b.id === SYSTEM_BUTTONS_DEFAULT_SCHEMA.SUBMIT.id ||
      b.id === SYSTEM_BUTTONS_DEFAULT_SCHEMA.SIGN_AND_SUBMIT.id ||
      b.id === INTEGRATION_BUTTONS_DEFAULT_SCHEMA.SUBMIT_AND_THIRDFORT.id
    ) {
      // render submit button

      let isWorldCheckButton =
        b.id === INTEGRATION_BUTTONS_DEFAULT_SCHEMA.SUBMIT_AND_THIRDFORT.id &&
        props.worldcheck?.id;

      let isThirdfortButton =
        !isWorldCheckButton &&
        b.id === INTEGRATION_BUTTONS_DEFAULT_SCHEMA.SUBMIT_AND_THIRDFORT.id;

      let isAMLChecksButton = isWorldCheckButton || isThirdfortButton;

      if ((!props.noSubmit && !props.isFormReadOnly) || isAMLChecksButton) {
        return (
          <Button
            $disableGutters
            type={props.docusign || isAMLChecksButton ? 'button' : 'submit'}
            data-testid={
              props.docusign
                ? 'docusign-form'
                : isThirdfortButton
                ? 'thirdfort-form'
                : isWorldCheckButton
                ? 'worldcheck-form'
                : 'submit-form'
            }
            variant={b.variant}
            customCSS={`${getButtonStyle(
              b.variant,
              b.color
            )} margin: 0 8px 0 0!important;`}
            id={
              b.id ===
              INTEGRATION_BUTTONS_DEFAULT_SCHEMA.SUBMIT_AND_THIRDFORT.id
                ? INTEGRATION_BUTTONS_DEFAULT_SCHEMA.SUBMIT_AND_THIRDFORT.id
                : undefined
            }
            onClick={
              isThirdfortButton
                ? props.onThirdfortSubmit
                : isWorldCheckButton
                ? props.onWorldCheckSubmit
                : props.docusign
                ? props.onDocusignSubmit
                : () => {}
            }
          >
            {props.docusign ? t('forms.docusign') : t(b.label)}
          </Button>
        );
      } else {
        return null;
      }
    }

    if (b.id === SYSTEM_BUTTONS_DEFAULT_SCHEMA.SAVE_CHANGES.id) {
      // render save changes button

      if (
        onlyOneButton ||
        (!props.useAutoSave &&
          !props.noSaveBtn &&
          !props.isFormReadOnly &&
          props.stickyBottomBtns) // ActionButtons used only for bottom buttons for now
      ) {
        <SaveChangesButton
          id={b.id}
          customCSS={`${getButtonStyle(b.variant, b.color)} margin: 0 8px 0 0;`}
          onClick={props.saveProgress}
          variant={b.variant}
        >
          {t(b.label)}
        </SaveChangesButton>;
      } else {
        return null;
      }
    }
    if (b.id === SYSTEM_BUTTONS_DEFAULT_SCHEMA.REQUEST_MORE_INFO.id) {
      // render request more info button
      return props.requestMoreInfo_TEMP_PROP;
    }

    const mpBtnOpts = {
      ...b,
      onClick: props.getCustomButtonOperation(b),
      customCSS: `svg {
        font-size: 20px;
      } ${IconsCSS} margin: 0 8px 0 0;`,
      muiBtnProps: {
        startIcon: <SCIcon />
      }
    };

    return (
      <MultipurposeButtonEngine
        key={`custom-button-${b.id}`}
        id={`multipurpose-button-${props.formId}`}
        options={[mpBtnOpts]}
      />
    );
  });
};
